import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Facility } from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import FacilityFormDialog from './FacilityFormDialog';

const FacilityList: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.FINANCING_MANAGE);
  const [rows, setRows] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await BorrowingService.listFacilities(institutionId));
    } catch (err) {
      toast.showApiError(err, 'Could not load the facilities');
    } finally {
      setLoading(false);
    }
  }, [institutionId, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box data-help-page="financing.list.page" sx={{ p: 2 }}>
      <Box data-help-section="financing.list.toolbar" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Bank borrowings</Typography>
        <Button data-help="financing.list.interest" component={RouterLink} to="/financing/interest">Interest runs</Button>
        <Button data-help="financing.list.reports" component={RouterLink} to="/financing/reports">Reports</Button>
        <Button data-help="financing.list.covenants" component={RouterLink} to="/financing/covenants">Covenants</Button>
        {canManage && <Button data-help="financing.list.new" variant="contained" onClick={() => setCreating(true)}>New facility</Button>}
      </Box>
      {!loading && rows.length === 0 && <Alert severity="info">No facilities yet. Create one to record drawdowns, schedules and interest.</Alert>}
      {rows.length > 0 && (
        <TableContainer data-help-section="financing.list.table" component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="financing.list.col.facility">Facility</TableCell><TableCell data-help="financing.list.col.type">Type</TableCell><TableCell data-help="financing.list.col.lender">Lender</TableCell>
                <TableCell data-help="financing.list.col.limit" align="right">Limit</TableCell><TableCell data-help="financing.list.col.rate" align="right">Rate %</TableCell>
                <TableCell data-help="financing.list.col.validTo">Valid to</TableCell><TableCell data-help="financing.list.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell><Link data-help="financing.list.action.open" component={RouterLink} to={`/financing/facilities/${r.id}`}>{r.facilityNo} - {r.name}</Link></TableCell>
                  <TableCell>{humanizeEnum(r.facilityType)}</TableCell>
                  <TableCell>{r.lenderName}</TableCell>
                  <TableCell align="right">{formatMoney(r.sanctionedLimit, r.currencyCode)}</TableCell>
                  <TableCell align="right">{r.currentRatePct ?? '-'}</TableCell>
                  <TableCell>{r.validTo ? formatDate(r.validTo) : '-'}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={r.status === 'ACTIVE' ? 'success' : 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <FacilityFormDialog open={creating} institutionId={institutionId} onClose={() => setCreating(false)}
        onSaved={f => { setCreating(false); navigate(`/financing/facilities/${f.id}`); }} />
    </Box>
  );
};

export default FacilityList;
